import { AppModule } from './../app/app.module';
import { tap, filter } from 'rxjs/operators';
import { AmplitudeService } from '../services/amplitude-service';
import { EventEmitter, Inject, Injectable } from "@angular/core";
import { environment } from "../environments/environment";
import { UserProfile } from "../models/userprofile";
import { Router, NavigationEnd, NavigationStart } from "@angular/router";
import { BehaviorSubject, from, Observable, of, pipe, Subject } from "rxjs";
import { AppConfig, AppConfigService } from "../services/app-config.service";
import { HttpClient } from "@angular/common/http";
import userflow from "userflow.js";
import { AccountInfo, AuthenticationResult, BrowserAuthError, EventMessage, EventType, InteractionStatus, RedirectRequest, SilentRequest } from '@azure/msal-browser';
import { OnDestroy } from '@angular/core';
import { map, takeUntil } from 'rxjs/operators';
import { MSAL_GUARD_CONFIG } from './msal/constants';
import { MsalGuardConfiguration } from './msal/msal-guard-config';
import { MsalBroadcastService } from './msal/msal-broadcast-service';
import { MsalService } from './msal/msal-service';
import { utilities } from 'src/utils/utilities';

declare const heap: any | undefined;

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  
  private readonly _destroying$ = new Subject<void>();
  userLoadededEvent: any = new EventEmitter<any>();//EventEmitter<User> = new EventEmitter<User>();

  currentUser: AccountInfo;
  loggedIn = false;
  public appConfig: AppConfig;
  private userflowIdentified: boolean = false;
  private interactionStatus: InteractionStatus = InteractionStatus.None;
  private msalEvent: EventMessage = undefined;

  private claims: UserProfile = new UserProfile();
  private claimsSubject: BehaviorSubject<UserProfile>;
  private claims$: Observable<UserProfile>;
1
  private gettingClaims = false;  
  private orgManagerPermission = "member_management__all__add_organization_manager";
  
  constructor(
    private router: Router,    
    private http: HttpClient,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private appConfigService: AppConfigService,
    private amplitude: AmplitudeService) {

    this.claimsSubject = new BehaviorSubject<UserProfile>(null);
    this.claims$ = this.claimsSubject.asObservable()
      .pipe(filter(c => c != null));

    router.events.subscribe((val: any) => {
      if (val instanceof NavigationEnd) {
        if (
          !val.url.startsWith("/auth-callback") &&
          !val.url.startsWith("/logoff") &&
          !val.url.startsWith("/logout") &&
          !val.url.startsWith("/login") &&
          !val.url.startsWith("/reset-password") &&
          val.url !== "/"
        ) {
          // save the latest navigation so we can get back there if we have to redirect
          sessionStorage.setItem("returnUrl", val.url);
        }
        this.amplitude.logUrlEvent(val.url);
      }

      if (val instanceof NavigationStart) {
        if (
          !val.url.startsWith("/auth-callback") &&
          !val.url.startsWith("/logoff") &&
          !val.url.startsWith("/logout") &&
          !val.url.startsWith("/login") &&
          !val.url.startsWith("/reset-password") &&
          val.url !== "/"
        ) {
          // save the latest navigation so we can get back there if we have to redirect
          sessionStorage.setItem("returnUrl", val.url);
        }
      }

      if (this.loggedIn !== true) return;

      if (!this.userflowIdentified) {
        this.userflowIdentified = true;
        userflow.identify(this.currentUser.idTokenClaims["sub"] as string, {
          name: this.currentUser.username,
          email: this.currentUser.username,
        }).catch(() => this.userflowIdentified = false);
      }
    });

    this.appConfig = this.appConfigService.getConfig();
    this.setupMsalListeners();
    this.initFacClaims();    
  }

  private setupMsalListeners() {
    this.msalBroadcastService.msalSubject$
      .pipe(takeUntil(this._destroying$))
      .subscribe((evnt: EventMessage) => {
        this.msalEvent = evnt;
        if (evnt.eventType === EventType.HANDLE_REDIRECT_END) {
          this.initFacClaims();
        } else if (evnt.eventType === EventType.LOGIN_FAILURE || evnt.eventType === EventType.ACQUIRE_TOKEN_FAILURE
                   || evnt.eventType === EventType.LOGOUT_FAILURE || evnt.eventType === EventType.SSO_SILENT_FAILURE) {
          this.router.navigate(["/error"], { queryParams: { error: evnt.error.name, message: evnt.error.message } });
        }
        if (environment.debug_mode) {
          console.log(evnt);
        }
      }, (e) => {
        console.error(e);
      });

    this.msalBroadcastService.inProgress$
      .pipe(takeUntil(this._destroying$))
      .subscribe((status: InteractionStatus) => {
        console.log(status);
        this.interactionStatus = status;
      }, (e) => {
        console.error(e);
      });
  }

  private initFacClaims() {
    if (this.gettingClaims === false) {
      if(this.isLoggedIn()){
      const accounts = this.msalService.instance.getAllAccounts();
      const account = accounts[0];
      this.currentUser = account;
      this.getClaimsFromServer().subscribe(c => {
        if (environment.debug_mode) {
          console.log(c);
        }
      }, (err) => {
        this.gettingClaims = false;
        console.error(err);
      });
     }
    }
  }

   private getClaimsFromServer(): Observable<UserProfile> {
    if (!this.claims.username) {
      const userId = this.currentUser.idTokenClaims["extension_userid"];
      if(!userId) {
        this.logout().subscribe(() => { });       
        return;
      }
      const url = `${this.appConfig.identity_api_url}${environment.identity_api_claims}?userid=${userId}`;
      this.gettingClaims = true;
      return this.http.get(url)
        .pipe(
          map((r: Array<{ type: string, value: string }>) => {            
            let result = r.reduce((map, obj) => (map[obj.type] = obj.value, map), {} as UserProfile);
            return result;
          }),
          tap(r => {
            this.claims = r;
            this.claims.sub = this.currentUser.idTokenClaims["extension_userid"] as string;
            this.claimsSubject.next(this.claims);
            this.gettingClaims = false;
          }));
    }
    return of(this.claims);
  }

  private redirectAfterSuccessSignIn() {
    if (sessionStorage.getItem("returnUrl") != null) {
      const redirect = sessionStorage.getItem("returnUrl");
      sessionStorage.removeItem("returnUrl");
      this.router.navigateByUrl(redirect);
    } else {
      this.router.navigate(["/cases/"]);
    }
  }  

  getUser() {
    return this.currentUser;
  }

  isLoggedIn(): boolean {
    return this.msalService.isLoggedIn();
    

  }

  getAccessToken(): Observable<string> {    
    const ocmScopes = [...environment.msal.protectedResources.ocmApi.scopes];
    return this.msalService.acquireAccessTokenSilent(ocmScopes);
  }

  getSignalRAccessToken(): Observable<string> {
    const ocmScopes = [...environment.msal.protectedResources.ocmApi.scopes];
    return this.msalService.acquireAccessTokenSilent(ocmScopes);
  }

  getIdToken(): Observable<string> {
    const ocmScopes = [...environment.msal.protectedResources.ocmApi.scopes];
    return this.msalService.acquireIdTokenSilent(ocmScopes);
  }

  getTokenType(): string {
    //return this.currentUser.token_type;
    return "token_type"
  } 

  getClaims(): UserProfile {
    return this.claims;
  }

  getOrganizationId(): string {
    return this.claims["extension_organizationid"]
  }

  getClaims$(): Observable<UserProfile> {
    return this.claims$;
  }


  login(): Observable<void> {
    if(this.interactionStatus !== InteractionStatus.None && this.interactionStatus !== InteractionStatus.Startup){
      //this.redirectAfterSuccessSignIn();
      return of(null);
    } 
    const isLoggedIn = this.msalService.isLoggedIn();    
    if (isLoggedIn) {
      this.redirectAfterSuccessSignIn()
      return of(null);
    }
    else {
      if (this.msalGuardConfig.authRequest) {
        return this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
      } else {
        return this.msalService.loginRedirect();
      }
    }
  }

  startAuthentication(): Observable<void> {
    if (this.interactionStatus !== InteractionStatus.None) return;
    if (this.msalGuardConfig.authRequest) {
      this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
    } else {
      this.msalService.loginRedirect();
    }    
  }

  completeAuthentication(action?: () => void): void {
    this.msalService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult) => {
        if (!result) return this.router.navigate(["/login"]);
        if (!this.currentUser) {
          this.msalService.instance.setActiveAccount(result.account);
          this.currentUser = result.account;
          console.log(result);
          if (action) action();                // userflow is initialised at the app entry point, but we can't identify until now.
          this.userflowIdentified = true;
          userflow.identify(this.currentUser.idTokenClaims["sub"] as string, {
            name: this.currentUser.username,
            email: this.currentUser.username,
          }).catch(() => this.userflowIdentified = false);
          if (action) action();
          this.initHeapAnalitics(this.currentUser, this);
          this.amplitude.initUser(this.currentUser.username);          
        }
      },
      error: (error) => console.log(error)
    });
  }

  resetPassword(userEmail: string) {
    this.msalService.handleRedirectObservable()
      .subscribe((authResult: AuthenticationResult) => {
        this.msalService.resetPassword(userEmail)
          .subscribe(() => {

          },
            (err: BrowserAuthError) => {
              console.error(err);
              if (err.errorCode === "interaction_in_progress") {
                const cookies = utilities.GetAllCookieValues();
                Object.keys(cookies)
                  .filter(k => k.startsWith("msal."))
                  .forEach(c => utilities.RemoveCookieValue(c));
                this.msalService.resetPassword(userEmail);
              }
            });
      });
  }

  initHeapAnalitics(user: any, that: AuthService): any {
    if (typeof heap !== "undefined" && that.appConfigService.getConfig().heap_key) {
      heap.identify(user.profile.username)
      heap.addUserProperties({ 'Roles': user.profile.role.join(','), 'Oranization Name' : user.profile.organization_short_name});
    }
  }

  isAdmin(): boolean {
    return this.claims && this.claims["role"] ? this.claims["role"].includes('fac-admin') : false;
    
  }

  isOrgManager(): boolean {    
    return this.claims && this.claims[this.orgManagerPermission];    
  }

  IsAnalyticsViewer(): boolean {
    return this.claims && this.claims["role"] ? this.claims["role"].includes('default-analytics-viewer') : false;
  }

  getUserRoles(): Array<string> {
    return this.claims && this.claims["role"] ? this.claims["role"].split(","):new Array<string>();
  }

  hasPermissionCenter(
    centerId: string,
    permission: string,
    category: string,
    subcategory: string
  ): boolean {
    
    let rtn = false;

    if (this.currentUser != null) {

      const userProfile = this.claims;
      const claim: string = category + "__" + subcategory + "__" + permission;

      const profile: string = userProfile[claim];

      if (profile === undefined) {
        // token does not have the required claim
        return false;
      } else {
        if (profile === "00000000-0000-0000-0000-000000000000") {
          // token has the required claim, but no centers
          return false;
        }
      }

      // profile = profile.toLowerCase();
      // centerId = centerId.toLowerCase();

      if (centerId.length > 0) {
        if (profile === "ffffffff-ffff-ffff-ffff-ffffffffffff") {
          // claim valid for all centers
          return true;
        }

        if (profile.includes(centerId)) {
          rtn = true;
        }
      } else {
        rtn = true;
      }
    }

    return rtn;
  }

  hasPermissionOLD(
    permission: string,
    category: string,
    subcategory: string
  ): boolean {
    
    let rtn = false;

    if (this.currentUser != null) {
      const userProfile = this.claims;

      const claim: string = category + "__" + subcategory;

      const profile: string = userProfile[claim];

      if (profile === undefined) {
        return false;
      }

      if (environment.debug_mode) {
        // console.log("hasPermission, profile: " + profile);
      }

      if (profile.includes(permission)) {
        rtn = true;
      }
    }

    return rtn;
  }

  logout(): Observable<void> {
    localStorage.clear();
    this.amplitude.initUser(null);
    const accToLogout = this.currentUser;
    if (accToLogout) {
      const authority = environment.msal.signinAuthority;
      return this.msalService.logout({ account: accToLogout, authority: authority });
    }
    return new Observable((r)=>r.next());
  }

  checkEmailExists(email: string, orgId: string): Observable<any> {
    let url: string = "";
    this.appConfig = this.appConfigService.getConfig();
    url =
      this.appConfig.identity_api_url +
      environment.identity_api_organizations_url +
      orgId +
      "/members/emailvalidation?email=" +
      encodeURIComponent(email);
    return this.http.get(url);
  }

  resendEmail(email: string): Observable<any> {    

    const url: string =
      this.appConfig.identity_api_url +      
      "api/resend-email-confirmation?email=" +
      encodeURIComponent(email);

    return this.http.post(url, {});
  }

  updateUserLanguage(langSelected: string) {
    this.msalService.updateLanguage(langSelected);
  }

  cleanUpMsalCookies() {
    const cookies = utilities.GetAllCookieValues();
    Object.keys(cookies)
      .filter(k => k.startsWith("msal."))
      .forEach(c => utilities.RemoveCookieValue(c));
  }

  ngOnDestroy(): void {
    this._destroying$.next(null);
    this._destroying$.complete();
  }
}
